import { Injectable, OnDestroy } from '@angular/core';
import { Observable, interval, switchMap, takeWhile, map, catchError, of, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpParams } from '@angular/common/http';
import { UssApiService } from '../../../features/uss-toolbox/services/uss-api/uss-api.service';
import { DownloadStatusService } from '../../../features/uss-toolbox/services/download-status/download-status.service';
import { 
  ExportInitResponse, 
  ExportFile, 
  PaginatedFilesResponse,
  ExportConfig,
  ExportFilters
} from '../../../models/export.model';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private isPolling = false;
  private activeJobIds: number[] = [];
  private pollingSubscription: Subscription | null = null;
  private largeExportJobIds: Set<number> = new Set();
  private readonly LARGE_EXPORT_STORAGE_KEY = 'largeExportJobIds';

  constructor(
    private downloadStatusService: DownloadStatusService,
    private toastrService: ToastrService,
    private api: UssApiService,
  ) {}

  /**
   * Save large export job IDs to localStorage
   */
  private saveLargeExportJobIds(): void {
    localStorage.setItem(
      this.LARGE_EXPORT_STORAGE_KEY,
      JSON.stringify([...this.largeExportJobIds])
    );
  }

  /**
   * Load large export job IDs from localStorage
   */
  private loadLargeExportJobIds(): void {
    const stored = localStorage.getItem(this.LARGE_EXPORT_STORAGE_KEY);
    if (stored) {
      try {
        this.largeExportJobIds = new Set(JSON.parse(stored));
      } catch {
        this.largeExportJobIds = new Set();
      }
    }
  }

  /**
   * Remove a job ID from large export tracking
   */
  private removeLargeExportJobId(jobId: number): void {
    this.largeExportJobIds.delete(jobId);
    this.saveLargeExportJobIds();
  }

  /**
   * Clear all large export job IDs from tracking and localStorage
   */
  private clearLargeExportJobIds(): void {
    this.largeExportJobIds.clear();
    localStorage.removeItem(this.LARGE_EXPORT_STORAGE_KEY);
  }

  /**
   * Initiate export for a table with filters
   * @param config Export configuration
   * @param countDown Count used to determine polling interval
   * @param filters Optional filters to apply
   * @param isLargeExport Flag to indicate long-running exports (e.g., spot_standard history)
   */
  exportTable(config: ExportConfig, countDown: number, filters: Record<string, string | number> = {}, isLargeExport: boolean = false): Observable<{ jobId: number; fileId: string }> {
    // Step 1: Initiate export job with filters as query parameters
    const url = `csv/export/${config.endpoint}`;
    
    // Convert filters object to HttpParams for query parameters
    let httpParams = new HttpParams();
    if (Object.keys(filters).length > 0) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          httpParams = httpParams.set(key, filters[key].toString());
        }
      });
    }
    
    const options = Object.keys(filters).length > 0 ? { params: httpParams } : {};
    
    return this.api.post(url, {}, options).pipe(
      switchMap((response: ExportInitResponse) => {
        if (response.status === 202 && response.data?.exportJobId) {
          // Add file to download status
          const fileId = this.downloadStatusService.addFile(
            response.data.exportJobId,
            response.data.fileName || `${config.displayName}.csv`
          );
          
          // Store job ID for polling
          this.activeJobIds.push(response.data.exportJobId);
          
          // Track large export job IDs for page refresh handling
          if (isLargeExport) {
            this.largeExportJobIds.add(response.data.exportJobId);
            this.saveLargeExportJobIds();
          }
          
          // Start polling if not already running
          if (!this.isPolling) {
            this.startPolling(countDown);
          }
          
          return of({ jobId: response.data.exportJobId, fileId });
        } else {
          this.toastrService.error('Failed to create export job');
          throw new Error('Failed to create export job');
        }
      }),
      catchError((error) => {
        this.toastrService.error(`Failed to export ${config.displayName}. Please try again.`);
        throw error;
      })
    );
  }

  /**
   * Start polling for export status
   */
  private startPolling(countDown: number): void {
    if (this.isPolling) return;
    
    this.isPolling = true;
    const pollingInterval = this.getPollingInterval(countDown);

    this.pollingSubscription = interval(pollingInterval).pipe(
      takeWhile(() => this.isPolling && this.activeJobIds.length > 0),
      switchMap(() => this.api.post('csv/files', { isPolling: true })),
      map((response: PaginatedFilesResponse) => {
        // Handle the new response structure with items array
        const files = response.data?.items || [];
        this.processFilesResponse(files);
        return response;
      }),
      catchError((error) => {
        console.error('Polling error:', error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Process files response and update download status
   */
  private processFilesResponse(files: ExportFile[]): void {
    // Find our active jobs
    const ourJobs = files.filter(file => this.activeJobIds.includes(file.id));
    
    ourJobs.forEach(job => {
      if (job.status === 'Completed' && job.downloadUrl) {
        // Job completed successfully
        
        // Update download status to completed
        this.downloadStatusService.updateFileStatus(
          job.id,
          'Completed',
          job.downloadUrl
        );
        
        this.toastrService.success(`Export completed successfully!`);
        
        // Remove from active jobs
        this.activeJobIds = this.activeJobIds.filter(id => id !== job.id);
        
        // Clean up large export tracking
        this.removeLargeExportJobId(job.id);
        
      } else if (job.status === 'Failed') {
        // Job failed
        
        this.downloadStatusService.updateFileStatus(job.id, 'Failed');
        this.toastrService.error('Export failed');
        
        // Remove from active jobs
        this.activeJobIds = this.activeJobIds.filter(id => id !== job.id);
        
        // Clean up large export tracking
        this.removeLargeExportJobId(job.id);
        
      } else {
        // Still in progress
        
        // Update status to in progress
        this.downloadStatusService.updateFileStatus(job.id, 'InProgress');
      }
    });
    
    // Stop polling if no active jobs
    if (this.activeJobIds.length === 0) {
      this.isPolling = false;
      this.unsubscribePolling();
      
      // Keep download status visible so user can see completed/failed files
      // Don't clear files - let user see the final status and download links
    }
  }

  /**
   * Get export configuration for different tables
   */
  getExportConfig(tableName: string): ExportConfig | null {
    const configs: { [key: string]: ExportConfig } = {
      'duration_factor': {
        tableName: 'duration_factor',
        endpoint: 'duration_factor',
        displayName: 'Duration Factors'
      },
      'segment_factor': {
        tableName: 'segment_factor',
        endpoint: 'segment_factor',
        displayName: 'Segment Factors'
      },
      'floor_multiplier': {
        tableName: 'floor_multiplier',
        endpoint: 'floor_multiplier',
        displayName: 'Floor Factors'
      },
      'peergroup_factor': {
        tableName: 'peergroup_factor',
        endpoint: 'peergroup_factor',
        displayName: 'Peer Group Factors'
      },
      'tier_factor': {
        tableName: 'tier_factor',
        endpoint: 'tier_factor',
        displayName: 'Tier Factors'
      },
      'opportunity_tier': {
        tableName: 'opportunity_tier',
        endpoint: 'opportunity_tier',
        displayName: 'Opportunity Tiers'
      },
      'fee_agreement': {
        tableName: 'fee_agreement',
        endpoint: 'fee_agreement',
        displayName: 'Fee Agreements'
      },
      'pricing_information': {
        tableName: 'pricing_information',
        endpoint: 'pricing_information',
        displayName: 'Pricing Information'
      },
      'pricing_agreement_line': {
        tableName: 'pricing_agreement_line',
        endpoint: 'pricing_agreement_line',
        displayName: 'Pricing Agreement Lines'
      },
      'permit_fee': {
        tableName: 'permit_fee',
        endpoint: 'permit_fee',
        displayName: 'Permit Fees'
      },
      'account': {
        tableName: 'account',
        endpoint: 'account',
        displayName: 'Accounts'
      },
      'sales_area': {
        tableName: 'sales_area',
        endpoint: 'sales_area',
        displayName: 'Sales Areas'
      },
      'zipcode_rate_zone': {
        tableName: 'zipcode_rate_zone',
        endpoint: 'zipcode_rate_zone',
        displayName: 'Zipcode Rate Zones'
      },
      'product': {
        tableName: 'product',
        endpoint: 'product',
        displayName: 'Products'
      },
      'price_rate_zone': {
        tableName: 'price_rate_zone',
        endpoint: 'price_rate_zone',
        displayName: 'Price Rate Zones'
      },
      'location': {
        tableName: 'location',
        endpoint: 'location',
        displayName: 'Locations'
      },
      'acquisition': {
        tableName: 'acquisition',
        endpoint: 'acquisition',
        displayName: 'Acquisitions'
      },
      'region': {
        tableName: 'region',
        endpoint: 'region',
        displayName: 'Regions'
      },
      'stock_prices': {
        tableName: 'stock_prices',
        endpoint: 'stock_prices',
        displayName: 'Stock Prices'
      },
      'non_stock_prices': {
        tableName: 'non_stock_prices',
        endpoint: 'non_stock_prices',
        displayName: 'Non Stock Prices'
      },
      'zz_region_history': {
        tableName: 'zz_region_history',
        endpoint: 'zz_region_history',
        displayName: 'Region History'
      },
      'price_validation': {
        tableName: 'price_validation',
        endpoint: 'price_validation',
        displayName: 'Pricing Validation'
      },
      'zz_price_rate_zone_history': {
        tableName: 'zz_price_rate_zone_history',
        endpoint: 'zz_price_rate_zone_history',
        displayName: 'Price Rate Zone History'
      },
      'zz_location_history': {
        tableName: 'zz_location_history',
        endpoint: 'zz_location_history',
        displayName: 'Location History'
      },
      'zz_product_history': {
        tableName: 'zz_product_history',
        endpoint: 'zz_product_history',
        displayName: 'Product History'
      },
      'zz_account_history': {
        tableName: 'zz_account_history',
        endpoint: 'zz_account_history',
        displayName: 'Account History'
      },
      'zz_acquisition_history': {
        tableName: 'zz_acquisition_history',
        endpoint: 'zz_acquisition_history',
        displayName: 'Acquisition History'
      },
      'zz_sales_area_history': {
        tableName: 'zz_sales_area_history',
        endpoint: 'zz_sales_area_history',
        displayName: 'Sales Area History'
      },
      'competitor': {
        tableName:'competitor_quote',
        endpoint: 'competitor_quote',
        displayName: 'Competitors'
      },
      'volume': {
        tableName: 'rate_card_performance_metric',
        endpoint: 'rate_card_volume',
        displayName: 'Volumes'
      },
      'median': {
        tableName: 'rate_card_performance_metric',
        endpoint: 'rate_card_median',
        displayName: 'Medians'
      },
      'zz_opportunity_tier_history': {
        tableName: 'zz_opportunity_tier_history',
        endpoint: 'zz_opportunity_tier_history',
        displayName: 'Opportunity Tier History'
      },
      'zz_permit_fee_history': {
        tableName: 'zz_permit_fee_history',
        endpoint: 'zz_permit_fee_history',
        displayName: 'Permit Fee History'
      },
      'zz_zipcode_rate_zone_history': {
        tableName: 'zz_zipcode_rate_zone_history',
        endpoint: 'zz_zipcode_rate_zone_history',
        displayName: 'Zipcode Rate Zone History'
      },
      'zz_fee_agreement_history': {
        tableName: 'zz_fee_agreement_history',
        endpoint: 'zz_fee_agreement_history',
        displayName: 'Fee Agreement History'
      },
      'zz_pricing_agreement_history': {
        tableName: 'zz_pricing_agreement_history',
        endpoint: 'zz_pricing_agreement_history',
        displayName: 'Pricing Information History'
      },
      'zz_pricing_agreement_line_history': {
        tableName: 'zz_pricing_agreement_line_history',
        endpoint: 'zz_pricing_agreement_line_history',
        displayName: 'Pricing Agreement Line History'
      },
      'zz_tier_factor_history': {
        tableName: 'zz_tier_factor_history',
        endpoint: 'zz_tier_factor_history',
        displayName: 'Tier Factor History'
      },
      'zz_segment_factor_history': {
        tableName: 'zz_segment_factor_history',
        endpoint: 'zz_segment_factor_history',
        displayName: 'Segment Factor History'
      },
      'zz_peer_group_factor_history': {
        tableName: 'zz_peer_group_factor_history',
        endpoint: 'zz_peer_group_factor_history',
        displayName: 'Peer Group Factor History'
      },
      'zz_floor_multiplier_history': {
        tableName: 'zz_floor_multiplier_history',
        endpoint: 'zz_floor_multiplier_history',
        displayName: 'Floor Multiplier Factor History'
      },
      'zz_duration_factor_history': {
        tableName: 'zz_duration_factor_history',
        endpoint: 'zz_duration_factor_history',
        displayName: 'Duration Factor History'
      }
    };
    return configs[tableName] || null;
  }

  /**
   * Stop polling and clear active jobs
   */
  stopPolling(): void {
    this.isPolling = false;
    this.activeJobIds = [];
    this.unsubscribePolling();
  }

  /**
   * Unsubscribe from polling subscription
   */
  private unsubscribePolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  /**
   * Get all files with pagination and filtering support
   */
  getAllFiles(page: number = 1, limit: number = 10, filters?: ExportFilters): Observable<PaginatedFilesResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    // Add filter parameters if provide
      if (filters?.search) {
        params = params.set('search', filters.search);
      }
      if (filters?.fromDate) {
        params = params.set('fromDate', filters.fromDate);
      }
      if (filters?.toDate) {
        params = params.set('toDate', filters.toDate);
      }
    
    return this.api.post('csv/files', { isPolling: false }, { params });
  }

  /**
   * Check for existing export jobs on app startup and restore state
   * This handles page refresh scenarios
   */
  checkForExistingJobs(): Observable<void> {
    // Load large export job IDs from localStorage
    this.loadLargeExportJobIds();
    
    // Use polling API to get only currently processing files
    return this.api.post('csv/files', { isPolling: true }).pipe(
      map((response: PaginatedFilesResponse) => {
        // Handle the new response structure with items array
        const files = response.data?.items || [];
        
        if (files.length > 0) {
          // Only show files that are currently being processed
          this.downloadStatusService.restoreExistingFiles(files);
          
          // Find jobs that are still in progress
          const inProgressJobs = files.filter(job => job.status === 'InProgress');
          
          if (inProgressJobs.length > 0) {
            // Restore active job tracking
            inProgressJobs.forEach(job => {
              this.activeJobIds.push(job.id);
            });
            
            // Start polling if we have active jobs
            if (this.activeJobIds.length > 0 && !this.isPolling) {
              // Check if any in-progress job is a large export
              const hasLargeExport = inProgressJobs.some(job => 
                this.largeExportJobIds.has(job.id)
              );
              const countHint = hasLargeExport ? 100000 : 5000;
              this.startPolling(countHint);
            }
          } else {
            // All jobs are completed/failed - clear large export tracking
            this.clearLargeExportJobIds();
          }
        } else {
          // No files at all - clear large export tracking
          this.clearLargeExportJobIds();
        }
      }),
      catchError((error) => {
        console.error('Error checking for existing jobs:', error);
        return of(void 0);
      })
    );
  }

  private getPollingInterval(countData: number): number {
    // Expand the download status panel
    this.downloadStatusService.updateDownloadStatus({ isCollapsed: false });
    const intervals = [
      { threshold: 1000, interval: 2000 },      // Up to 1k: poll every 2 seconds
      { threshold: 50000, interval: 5000 },     // Up to 50k: poll every 5 seconds
      { threshold: 100000, interval: 30000 },   // Up to 100k: poll every 30 seconds
      { threshold: 500000, interval: 60000 },   // Up to 500k: poll every 1 minute
      { threshold: 1000000, interval: 120000 }, // Up to 1M: poll every 2 minutes
    ];
    const match = intervals.find(({ threshold }) => countData <= threshold);
    return match?.interval ?? 180000; // Default to 3 minutes for very large datasets
  }
} 